import React, { useEffect, useState, useCallback } from 'react';
import {
  Loader2, UserSearch, Target, ShieldAlert, Building2,
  Globe, X, Plus, ArrowRight, CheckCircle2, AlertTriangle, Link2, Mail,
  BadgeCheck, BadgeAlert, Users, Info, Copy, ExternalLink, Search,
  ListFilter, RotateCcw, PhoneCall, Flame,
} from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NICHE_OPTIONS = ['Accounting & Finance', 'Healthcare', 'Legal', 'Logistics & Supply Chain'];
const READINESS_OPTIONS = ['ready', 'partial', 'stale', 'unknown', 'negative'];

const NICHE_COLORS = {
  'Accounting & Finance': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Healthcare: 'bg-blue-50 text-blue-700 border-blue-200',
  Legal: 'bg-violet-50 text-violet-700 border-violet-200',
  'Logistics & Supply Chain': 'bg-orange-50 text-orange-700 border-orange-200',
};

const READINESS_STYLES = {
  ready: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  stale: 'bg-gray-100 text-gray-500 border-gray-200',
  unknown: 'bg-gray-100 text-gray-500 border-gray-200',
  negative: 'bg-red-50 text-red-700 border-red-200',
};

const ACTION_STYLES = {
  CONTACT_TODAY: 'bg-emerald-600 text-white',
  ENRICH_FIRST: 'bg-amber-500 text-white',
  FOLLOW_UP: 'bg-blue-500 text-white',
  WATCH: 'bg-gray-400 text-white',
  NOT_SHOWN: 'bg-gray-300 text-gray-600',
};

const WARNING_LABELS = {
  identity_duplicate: { label: 'Duplicate Identity', style: 'bg-amber-50 text-amber-700 border-amber-200' },
  identity_conflict: { label: 'Unresolved Conflict', style: 'bg-red-50 text-red-700 border-red-200' },
  identity_unverified: { label: 'Unverified Identity', style: 'bg-gray-100 text-gray-500 border-gray-200' },
  size_unresolved: { label: 'Size Unresolved', style: 'bg-gray-100 text-gray-500 border-gray-200' },
  outreach_ambiguous: { label: 'Outreach Ambiguous', style: 'bg-amber-50 text-amber-700 border-amber-200' },
  opt_out: { label: 'Opt-Out', style: 'bg-red-50 text-red-700 border-red-200' },
  negative_response: { label: 'Negative Response', style: 'bg-red-50 text-red-700 border-red-200' },
  excluded: { label: 'Excluded', style: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const copyText = (text, label) => {
  if (!text) { toast.error(`No ${label} to copy`); return; }
  navigator.clipboard.writeText(text);
  toast.success(`Copied ${label}`);
};

const WarningBadges = ({ warnings }) => {
  if (!warnings || warnings.length === 0) return null;
  return (
    <>
      {warnings.map(w => {
        const meta = WARNING_LABELS[w] || { label: w, style: 'bg-gray-100 text-gray-500 border-gray-200' };
        return (
          <span key={w} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.style}`} data-testid={`warning-badge-${w}`}>
            {meta.label}
          </span>
        );
      })}
    </>
  );
};

const ScoreBar = ({ label, value, max }) => (
  <div>
    <div className="flex items-center justify-between text-[11px] text-gray-500 mb-0.5">
      <span>{label}</span><span className="font-semibold text-gray-700">{value}/{max}</span>
    </div>
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full bg-[#0A1F44] rounded-full" style={{ width: `${(value / max) * 100}%` }} />
    </div>
  </div>
);

const MetricChip = ({ label, value, icon: Icon, testId }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-1 min-w-[180px]" data-testid={testId}>
    <div className="h-9 w-9 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-[#0A1F44]" />
    </div>
    <div>
      <p className="text-xl font-bold text-[#0A1F44] leading-tight">{value}</p>
      <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
    </div>
  </div>
);

const ApolloStatusPill = ({ status }) => {
  if (!status) return null;
  const peopleOk = status.people_discovery_available;
  const orgOk = status.organization_enrichment_available;
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="apollo-status-pill">
      <span className="text-gray-400 font-medium">Apollo:</span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border ${orgOk ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`} data-testid="apollo-org-status">
        {orgOk ? <BadgeCheck className="h-3 w-3" /> : <BadgeAlert className="h-3 w-3" />} Org Enrichment {orgOk ? 'Available' : 'Unknown'}
      </span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border ${peopleOk === false ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`} data-testid="apollo-people-status">
        {peopleOk === false ? <ShieldAlert className="h-3 w-3" /> : <BadgeCheck className="h-3 w-3" />} People Discovery {peopleOk === false ? 'BLOCKED_APOLLO_PLAN' : 'Unknown'}
      </span>
      <span className="text-gray-400">
        Last real contact: {status.last_successful_contact_discovery_at ? new Date(status.last_successful_contact_discovery_at).toLocaleDateString() : 'Never'}
      </span>
    </div>
  );
};

const getLocalDateStr = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const ContactForm = ({ company, onSubmitted }) => {
  const [form, setForm] = useState({
    first_name: '', last_name: '', title: '', email: '', linkedin_url: '',
    source: 'LinkedIn', notes: '', verification_status: 'unverified',
  });
  const [submitting, setSubmitting] = useState(false);
  const today = getLocalDateStr();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.title.trim()) {
      toast.error('Real first name, last name, and title are required');
      return;
    }
    setSubmitting(true);
    try {
      const result = await fetchWithAuth(`${API_URL}/api/intelligence/enrich-queue/${encodeURIComponent(company)}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, date_researched: getLocalDateStr() }),
      });
      toast.success('Contact recorded');
      onSubmitted(result);
      setForm({ first_name: '', last_name: '', title: '', email: '', linkedin_url: '', source: 'LinkedIn', notes: '', verification_status: 'unverified' });
    } catch (e) {
      toast.error('Failed to add contact');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3" data-testid="manual-contact-form">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-[#0A1F44]">Enter Verified Contact</h4>
        <span className="text-[10px] text-gray-400">Researched: {today}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="contact-first-name-input" placeholder="First name *" value={form.first_name} onChange={e => set('first_name', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        <input data-testid="contact-last-name-input" placeholder="Last name *" value={form.last_name} onChange={e => set('last_name', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </div>
      <input data-testid="contact-title-input" placeholder="Title *" value={form.title} onChange={e => set('title', e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="contact-email-input" placeholder="Email (optional)" value={form.email} onChange={e => set('email', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        <input data-testid="contact-linkedin-input" placeholder="LinkedIn URL (optional)" value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="contact-source-select" value={form.source} onChange={e => set('source', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
          {['LinkedIn', 'Company Website', 'Apollo UI', 'Existing CRM', 'Manual Research', 'Other'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="contact-verification-select" value={form.verification_status} onChange={e => set('verification_status', e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="unverified">Unverified</option>
          <option value="verified">Verified</option>
        </select>
      </div>
      <textarea data-testid="contact-notes-input" placeholder="Notes" value={form.notes} onChange={e => set('notes', e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" rows={2} />
      <p className="text-[11px] text-gray-400">Only affects readiness if an email or LinkedIn URL is provided — a name + title alone will not count as a usable contact.</p>
      <button onClick={submit} disabled={submitting} data-testid="submit-contact-btn"
        className="w-full flex items-center justify-center gap-2 bg-[#0A1F44] text-white rounded-lg py-2 text-sm font-semibold hover:bg-[#0A1F44]/90 disabled:opacity-50">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Contact
      </button>
    </div>
  );
};

const ResearchHelpers = ({ company, domain, matrix }) => {
  const primaryTitle = matrix?.primary?.[0];
  const searchPhrase = primaryTitle ? `"${company}" "${primaryTitle}"` : `"${company}"`;
  const linkedinUrl = `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent((primaryTitle || '') + ' ' + company)}`;
  const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(searchPhrase)}`;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2" data-testid="research-helpers">
      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Manual Research Helpers</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => copyText(company, 'company name')} data-testid="copy-company-btn"
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><Copy className="h-3 w-3" /> Company</button>
        <button onClick={() => copyText(domain, 'domain')} data-testid="copy-domain-btn" disabled={!domain}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40"><Copy className="h-3 w-3" /> Domain</button>
        <button onClick={() => copyText(matrix?.primary?.join(', '), 'primary titles')} data-testid="copy-titles-btn"
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><Copy className="h-3 w-3" /> Primary Titles</button>
        <button onClick={() => copyText(searchPhrase, 'search phrase')} data-testid="copy-search-phrase-btn"
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"><Copy className="h-3 w-3" /> Search Phrase</button>
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={linkedinUrl} target="_blank" rel="noreferrer" data-testid="open-linkedin-search-link"
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"><ExternalLink className="h-3 w-3" /> LinkedIn Search</a>
        <a href={googleUrl} target="_blank" rel="noreferrer" data-testid="open-google-search-link"
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"><Search className="h-3 w-3" /> Web Search</a>
      </div>
      <p className="text-[10px] text-gray-400 italic">"{searchPhrase}" — no scraping, no invented emails. Human research only.</p>
    </div>
  );
};

const AccountDetailDrawer = ({ company, onClose, onQueueRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [transition, setTransition] = useState(null);
  const [visible, setVisible] = useState(false);

  const load = (preserveTransition = false) => {
    setLoading(true);
    if (!preserveTransition) setTransition(null);
    fetchWithAuth(`${API_URL}/api/intelligence/enrich-queue/${encodeURIComponent(company)}`)
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [company]);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t); }, []);

  const handleSubmitted = (result) => {
    load(true);
    setTransition(result);
    onQueueRefresh();
  };

  const requestClose = () => { setVisible(false); setTimeout(onClose, 200); };

  const os = data?.outreach_signal;
  const matrix = data?.targeting_matrix;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none" data-testid="enrich-account-drawer">
      <div
        className={`absolute top-0 right-0 h-full w-full sm:w-[42%] min-w-[380px] max-w-2xl bg-white shadow-2xl border-l border-gray-200 overflow-y-auto transition-transform duration-200 pointer-events-auto ${visible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-[#0A1F44]">{company}</h2>
            {data?.identity?.aliases?.length > 1 && (
              <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5" data-testid="identity-alias-note">
                <Users className="h-3 w-3" /> Known aliases: {data.identity.aliases.join(', ')}
              </p>
            )}
          </div>
          <button onClick={requestClose} data-testid="close-enrich-drawer" className="p-1 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading...</div>
        ) : !data ? (
          <div className="p-6 text-sm text-gray-500">Not found.</div>
        ) : (
          <div className="p-6 space-y-5">
            {transition && (
              <div className={`rounded-xl p-4 border-2 ${transition.transitioned_to_contact_today ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`} data-testid="transition-result-banner">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0A1F44] mb-2">
                  {transition.transitioned_to_contact_today ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Info className="h-4 w-4 text-gray-500" />}
                  Contact Recorded — Readiness Recalculated
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className={`px-2 py-1 rounded font-semibold ${ACTION_STYLES[transition.before.recommended_action]}`}>{transition.before.recommended_action}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
                  <span className={`px-2 py-1 rounded font-semibold ${ACTION_STYLES[transition.after.recommended_action]}`} data-testid="after-action-badge">{transition.after.recommended_action}</span>
                </div>
                {!transition.is_usable_for_readiness && (
                  <p className="text-[11px] text-gray-500 mt-2">This contact has no email/LinkedIn on file, so it does not count toward readiness yet — action unchanged.</p>
                )}
              </div>
            )}

            {/* ACCOUNT */}
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">Account</p>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${ACTION_STYLES[data.recommended_action]}`} data-testid="current-action-badge">{data.recommended_action}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${NICHE_COLORS[data.niche] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{data.niche || 'No niche match'}</span>
                <span className="text-lg font-bold text-[#0A1F44] ml-auto">{data.canonical_score}<span className="text-xs text-gray-400">/100</span></span>
              </div>
              <div className="grid grid-cols-2 gap-3 bg-white border border-gray-200 rounded-xl p-3" data-testid="canonical-score-components">
                <ScoreBar label="Hiring Signal" value={data.dimensions.hiring_signal_strength.score} max={35} />
                <ScoreBar label="Commercial Fit" value={data.dimensions.commercial_fit.score} max={35} />
                <ScoreBar label="Data Confidence" value={data.dimensions.data_confidence.score} max={15} />
                <ScoreBar label="Outreach Readiness" value={data.dimensions.outreach_readiness.score} max={15} />
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">
                Data Confidence reflects firmographic data consistency (employee count vs. hiring volume) — it is a separate signal from company-identity/domain verification shown below.
              </p>
              {data.verified_domain ? (
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2"><Globe className="h-3 w-3" /> Verified domain: {data.verified_domain}</p>
              ) : (
                <p className="text-xs text-gray-400 flex items-center gap-1 mt-2"><Globe className="h-3 w-3" /> Domain not yet verified</p>
              )}
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                <WarningBadges warnings={data.warnings} />
              </div>
            </div>

            {/* WHY NOW */}
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">Why Now</p>
              <ul className="space-y-1">
                {data.why_prioritized.map((reason, i) => (
                  <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5"><Flame className="h-3 w-3 text-amber-500 flex-shrink-0 mt-0.5" /> {reason}</li>
                ))}
              </ul>
            </div>

            {os?.ambiguous && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-testid="outreach-ambiguous-warning">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">{os.ambiguous_reason}</p>
              </div>
            )}
            {os?.opt_out && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2" data-testid="outreach-optout-warning">
                <ShieldAlert className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-800">Manual opt-out override is active for this company.</p>
              </div>
            )}

            {/* WHO TO FIND */}
            {matrix && (
              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Target className="h-3.5 w-3.5" /> Who To Find — {data.niche || 'Default'}</p>
                <div className="space-y-2">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase mb-1">Primary</p>
                    <p className="text-xs text-emerald-800">{matrix.primary.join(', ')}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                    <p className="text-[10px] font-bold text-blue-700 uppercase mb-1">Secondary</p>
                    <p className="text-xs text-blue-800">{matrix.secondary.join(', ')}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
                    <p className="text-[10px] font-bold text-red-700 uppercase mb-1">Avoid / Low-Value</p>
                    <p className="text-xs text-red-800">{matrix.avoid.join(', ')}</p>
                  </div>
                  {matrix.size_exception && <p className="text-[11px] text-gray-500 italic">{matrix.size_exception}</p>}
                </div>
              </div>
            )}

            <ResearchHelpers company={company} domain={data.verified_domain} matrix={matrix} />

            {/* CONTACTS */}
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Contacts ({data.contacts.length})</p>
              {data.contacts.length === 0 ? (
                <p className="text-xs text-gray-400">No contacts on file yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.contacts.map((c, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-lg p-2.5 text-xs flex items-center justify-between" data-testid={`existing-contact-${i}`}>
                      <div>
                        <p className="font-semibold text-[#0A1F44]">{c.contact_name || c.name || '(no name — placeholder)'}</p>
                        <p className="text-gray-500">{c.title} {c.source && <span className="text-gray-400">· {c.source}</span>}</p>
                        {c.date_researched && <p className="text-[10px] text-gray-400">Researched {c.date_researched} · {c.verification_status || 'unverified'}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {c.email && <Mail className="h-3.5 w-3.5 text-emerald-600" />}
                        {c.linkedin_url && <Link2 className="h-3.5 w-3.5 text-blue-600" />}
                        {!c.email && !c.linkedin_url && <span className="text-gray-400 text-[10px]">Unusable</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <ContactForm company={company} onSubmitted={handleSubmitted} />

            {/* OUTREACH HISTORY */}
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><PhoneCall className="h-3.5 w-3.5" /> Outreach History</p>
              <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs space-y-1">
                <p><span className="text-gray-500">Prior outreach:</span> <span className="font-medium text-[#0A1F44]">{os?.has_prior_outreach ? (os.last_outreach_date || 'Yes') : 'None on file'}</span></p>
                <p><span className="text-gray-500">Follow-up due:</span> <span className="font-medium text-[#0A1F44]">{os?.follow_up_due ? 'Yes' : 'No'}</span></p>
                <p><span className="text-gray-500">Response status:</span> <span className="font-medium text-[#0A1F44]">{os?.response_status || 'None'}</span></p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FiltersBar = ({ filters, setFilters }) => {
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clear = () => setFilters({ niche: '', readiness: '', identity: '', has_contact: '', min_score: '', max_score: '' });
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-2 flex-wrap" data-testid="queue-filters-bar">
      <span className="flex items-center gap-1 text-xs text-gray-400 font-semibold"><ListFilter className="h-3.5 w-3.5" /> Filters:</span>
      <select data-testid="filter-niche-select" value={filters.niche} onChange={e => set('niche', e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
        <option value="">All Niches</option>
        {NICHE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <select data-testid="filter-readiness-select" value={filters.readiness} onChange={e => set('readiness', e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
        <option value="">All Readiness</option>
        {READINESS_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
      <select data-testid="filter-identity-select" value={filters.identity} onChange={e => set('identity', e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
        <option value="">Identity: All</option>
        <option value="verified">Verified</option>
        <option value="warning">Warning</option>
      </select>
      <select data-testid="filter-has-contact-select" value={filters.has_contact} onChange={e => set('has_contact', e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
        <option value="">Contact: All</option>
        <option value="yes">Has Contact</option>
        <option value="no">No Contact</option>
      </select>
      <input data-testid="filter-min-score-input" type="number" placeholder="Min score" value={filters.min_score} onChange={e => set('min_score', e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-24" />
      <input data-testid="filter-max-score-input" type="number" placeholder="Max score" value={filters.max_score} onChange={e => set('max_score', e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-24" />
      <button onClick={clear} data-testid="clear-filters-btn" className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 ml-auto"><RotateCcw className="h-3 w-3" /> Clear</button>
    </div>
  );
};

export default function EnrichmentQueue({ embedded = false }) {
  const [tab, setTab] = useState('enrich_first');
  const [queue, setQueue] = useState([]);
  const [totalMatching, setTotalMatching] = useState(0);
  const [contactToday, setContactToday] = useState([]);
  const [summary, setSummary] = useState(null);
  const [apolloStatus, setApolloStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ niche: '', readiness: '', identity: '', has_contact: '', min_score: '', max_score: '' });

  const loadSummaryAndStatus = () => {
    Promise.all([
      fetchWithAuth(`${API_URL}/api/intelligence/enrich-queue/summary`),
      fetchWithAuth(`${API_URL}/api/intelligence/enrich-queue/apollo-status`),
    ]).then(([s, a]) => { setSummary(s); setApolloStatus(a); }).catch(() => {});
  };

  const loadQueue = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '25' });
    if (filters.niche) params.set('niche', filters.niche);
    if (filters.readiness) params.set('readiness', filters.readiness);
    if (filters.identity) params.set('identity', filters.identity);
    if (filters.has_contact) params.set('has_contact', filters.has_contact);
    if (filters.min_score) params.set('min_score', filters.min_score);
    if (filters.max_score) params.set('max_score', filters.max_score);
    fetchWithAuth(`${API_URL}/api/intelligence/enrich-queue?${params.toString()}`)
      .then(d => { setQueue(d.queue || []); setTotalMatching(d.total_matching || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [filters]);

  const loadContactToday = () => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/enrich-queue/contact-today`)
      .then(d => setContactToday(d.accounts || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadSummaryAndStatus(); }, []);
  useEffect(() => { if (tab === 'enrich_first') loadQueue(); else loadContactToday(); }, [tab, loadQueue]);

  const refreshAll = () => { loadSummaryAndStatus(); if (tab === 'enrich_first') loadQueue(); else loadContactToday(); };

  return (
    <IntelligenceLayout title="Enrichment Queue" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 bg-amber-50 rounded-xl flex items-center justify-center">
            <UserSearch className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="enrich-queue-title">Enrichment Queue</h1>
            <p className="text-sm text-gray-500">Internal / admin-only research pilot — not the production intelligence dashboard.</p>
          </div>
        </div>
        <ApolloStatusPill status={apolloStatus} />
      </header>

      <main className="p-8 space-y-4">
        {summary && (
          <div className="flex flex-wrap gap-3" data-testid="queue-summary-metrics">
            <MetricChip label="Enrich First" value={summary.enrich_first_count} icon={Flame} testId="metric-enrich-first" />
            <MetricChip label="Contact Today" value={summary.contact_today_count} icon={PhoneCall} testId="metric-contact-today" />
            <MetricChip label="Contacts Added Today" value={summary.contacts_added_today} icon={Plus} testId="metric-contacts-added-today" />
            <MetricChip label="Identity / Data Warnings" value={summary.identity_data_warning_count} icon={AlertTriangle} testId="metric-identity-warnings" />
          </div>
        )}

        <div className="flex items-center gap-2 border-b border-gray-200" data-testid="queue-tabs">
          <button onClick={() => setTab('enrich_first')} data-testid="tab-enrich-first"
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === 'enrich_first' ? 'border-[#0A1F44] text-[#0A1F44]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            Enrich First Queue
          </button>
          <button onClick={() => setTab('contact_today')} data-testid="tab-contact-today"
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === 'contact_today' ? 'border-[#0A1F44] text-[#0A1F44]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            Contact Today (Pilot)
          </button>
        </div>

        {tab === 'enrich_first' && <FiltersBar filters={filters} setFilters={setFilters} />}

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading...
          </div>
        ) : tab === 'enrich_first' ? (
          <>
            <p className="text-xs text-gray-400" data-testid="queue-total-matching">{totalMatching} matching account(s), showing top {queue.length} by canonical priority</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="enrich-queue-list">
              {queue.map((item, i) => (
                <button key={item.company} onClick={() => setSelected(item.company)} data-testid={`enrich-queue-card-${i}`}
                  className="text-left bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-[#D4AF37]/40 transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-[#0A1F44] flex items-center gap-1.5"><Building2 className="h-4 w-4 text-gray-400" /> {item.company}</h3>
                    <span className="text-lg font-bold text-[#0A1F44]">{item.canonical_score}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${NICHE_COLORS[item.niche] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{item.niche || 'No niche'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${READINESS_STYLES[item.readiness_status]}`}>{item.readiness_status}</span>
                    {item.known_aliases && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1" data-testid={`alias-badge-${i}`}>
                        <Users className="h-2.5 w-2.5" /> {item.known_aliases.length} aliases
                      </span>
                    )}
                    <WarningBadges warnings={item.warnings} />
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-1">{item.why_prioritized?.[0]}</p>
                  <div className="flex items-center justify-between mt-2 text-[11px] text-gray-400">
                    <span>{item.current_contact_coverage.contacts_on_file} contact(s) on file</span>
                    <span className="text-[#0A1F44] font-semibold flex items-center gap-1">View & Enrich <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </button>
              ))}
              {queue.length === 0 && <p className="text-sm text-gray-400 col-span-2 text-center py-10">No accounts match the current filters.</p>}
            </div>
          </>
        ) : (
          <div className="space-y-3" data-testid="contact-today-list">
            {contactToday.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-10" data-testid="contact-today-empty">No accounts have reached CONTACT_TODAY yet — add a usable contact to an Enrich First account to move it here.</p>
            ) : contactToday.map((item, i) => (
              <div key={item.company} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid={`contact-today-card-${i}`}>
                <div>
                  <h3 className="font-semibold text-[#0A1F44] flex items-center gap-1.5"><Building2 className="h-4 w-4 text-gray-400" /> {item.company}</h3>
                  <p className="text-xs text-gray-600 mt-1">{item.decision_maker || 'Unknown contact'} — {item.title || 'No title on file'}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{item.why_prioritized?.[0]}</p>
                  {item.outreach_signal?.ambiguous && <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {item.outreach_signal.ambiguous_reason}</p>}
                </div>
                <div className="text-right">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${ACTION_STYLES[item.recommended_action]}`}>{item.recommended_action}</span>
                  <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1 justify-end">
                    {item.contact_method_available === 'email' && <><Mail className="h-3 w-3" /> Email available</>}
                    {item.contact_method_available === 'linkedin' && <><Link2 className="h-3 w-3" /> LinkedIn available</>}
                    {item.contact_method_available === 'none' && 'No contact method'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {selected && <AccountDetailDrawer company={selected} onClose={() => setSelected(null)} onQueueRefresh={refreshAll} />}
    </IntelligenceLayout>
  );
}
